import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { CheckCircle2, Trash2, Plus, Minus, ChevronRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import useSWR from 'swr';
import { publicSettings } from '../lib/api';

export const Cart = () => {
  const { cart, removeFromCart, updateQty, total } = useCart();

  return (
    <div className="bg-white min-h-[60vh]">
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-xs text-neutral-500 mb-4">
          <Link to="/" className="hover:text-[#b8892d]">Home</Link>
          <ChevronRight size={12}/>
          <span className="text-neutral-800">Shopping Basket</span>
        </div>
        <h1 className="font-serif text-3xl md:text-4xl text-[#1a1a1a] mb-8">Shopping Basket</h1>
        {cart.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-neutral-600 mb-4">Your basket is empty.</p>
            <Link to="/shop"><Button className="bg-[#b8892d] hover:bg-[#a07826]">Continue Shopping</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-3">
              {cart.map(item => (
                <div key={item.id} className="flex gap-4 p-4 border border-neutral-200 items-center">
                  <img src={item.image} alt={item.title} className="w-24 h-24 object-cover bg-neutral-50"/>
                  <div className="flex-1">
                    <h3 className="font-medium text-neutral-800 mb-1">{item.title}</h3>
                    <div className="text-xs text-[#b8892d] uppercase tracking-widest mb-2">{item.brand}</div>
                    <div className="text-[#b8892d] font-semibold">₹{item.sale.toLocaleString('en-IN')}</div>
                  </div>
                  <div className="flex items-center border border-neutral-300">
                    <button onClick={() => updateQty(item.id, item.qty - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100"><Minus size={12}/></button>
                    <span className="w-10 text-center text-sm">{item.qty}</span>
                    <button onClick={() => updateQty(item.id, item.qty + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100"><Plus size={12}/></button>
                  </div>
                  <button onClick={() => removeFromCart(item.id)} className="text-neutral-400 hover:text-red-600"><Trash2 size={18}/></button>
                </div>
              ))}
            </div>
            <div className="bg-neutral-50 p-6 h-fit sticky top-32">
              <h3 className="font-serif text-xl mb-4">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>₹{total.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between"><span>Shipping</span><span className="text-green-700">Free</span></div>
                <div className="flex justify-between font-semibold text-base pt-3 border-t border-neutral-200"><span>Total</span><span className="text-[#b8892d] text-lg">₹{total.toLocaleString('en-IN')}</span></div>
              </div>
              <Link to="/checkout">
                <Button className="w-full mt-5 bg-[#b8892d] hover:bg-[#a07826] h-11 uppercase tracking-widest text-xs">Proceed to Checkout</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const Checkout = () => {
  const { cart, total, clearCart } = useCart();
  const { toast } = useToast();
  const { data: settings } = useSWR('publicSettings', publicSettings);
  const siteName = settings?.siteName || 'Watch Trader';
  const [placed, setPlaced] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', pincode: '', payment: 'cod' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address) {
      toast({ title: 'Missing info', description: 'Please fill in all required fields.' });
      return;
    }
    setPlaced(true);
    setTimeout(() => clearCart(), 500);
  };

  if (placed) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <CheckCircle2 size={64} className="mx-auto text-green-600 mb-5"/>
        <h1 className="font-serif text-3xl mb-3">Order Placed Successfully!</h1>
        <p className="text-neutral-600 mb-6">Thank you for shopping with {siteName}. Our team will contact you shortly to confirm your order and delivery details.</p>
        <Link to="/"><Button className="bg-[#b8892d] hover:bg-[#a07826]">Back to Home</Button></Link>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <p className="text-neutral-600 mb-4">Your basket is empty.</p>
        <Link to="/shop"><Button className="bg-[#b8892d] hover:bg-[#a07826]">Continue Shopping</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="font-serif text-3xl md:text-4xl mb-8">Checkout</h1>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-neutral-200 p-6 space-y-4">
            <h3 className="font-semibold text-lg">Billing Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Full Name *</Label><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1"/></div>
              <div><Label>Phone *</Label><Input required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1"/></div>
              <div className="md:col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1"/></div>
              <div className="md:col-span-2"><Label>Address *</Label><Input required value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="mt-1"/></div>
              <div><Label>City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="mt-1"/></div>
              <div><Label>Pincode</Label><Input value={form.pincode} onChange={e => setForm({...form, pincode: e.target.value})} className="mt-1"/></div>
            </div>
          </div>
          <div className="border border-neutral-200 p-6">
            <h3 className="font-semibold text-lg mb-4">Payment Method</h3>
            <RadioGroup value={form.payment} onValueChange={(v) => setForm({...form, payment: v})}>
              {[{v:'cod',l:'Cash on Delivery (10% advance)'},{v:'upi',l:'UPI · Paytm · Google Pay · PhonePe'},{v:'card',l:'Debit / Credit Card'},{v:'bank',l:'Bank Transfer'}].map(o => (
                <div key={o.v} className="flex items-center space-x-2 py-2">
                  <RadioGroupItem value={o.v} id={o.v}/>
                  <Label htmlFor={o.v} className="cursor-pointer">{o.l}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>
        <div className="bg-neutral-50 p-6 h-fit sticky top-32">
          <h3 className="font-serif text-xl mb-4">Your Order</h3>
          <div className="space-y-3 text-sm mb-4">
            {cart.map(i => (
              <div key={i.id} className="flex justify-between gap-2">
                <span className="text-neutral-700 flex-1 line-clamp-1">{i.title} × {i.qty}</span>
                <span>₹{(i.sale * i.qty).toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm pt-3 border-t border-neutral-200">
            <div className="flex justify-between"><span>Subtotal</span><span>₹{total.toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span className="text-green-700">Free</span></div>
            <div className="flex justify-between font-semibold text-base pt-3 border-t border-neutral-200"><span>Total</span><span className="text-[#b8892d] text-lg">₹{total.toLocaleString('en-IN')}</span></div>
          </div>
          <Button type="submit" className="w-full mt-5 bg-[#b8892d] hover:bg-[#a07826] h-11 uppercase tracking-widest text-xs">Place Order</Button>
        </div>
      </form>
    </div>
  );
};
